/**
 * State Management Module
 *
 * Manages conversation history, session data, and localStorage persistence.
 */

// Session state constants
export const SESSION_STATES = {
  DISCONNECTED: 'DISCONNECTED',
  CONNECTING: 'CONNECTING',
  CONNECTED: 'CONNECTED',  // WebSocket open, waiting for backend ready message
  IN_QUEUE: 'IN_QUEUE',
  ACTIVE_CHAT: 'ACTIVE_CHAT',
  IDLE_WARNING: 'IDLE_WARNING',
  CONVERSATION_END: 'CONVERSATION_END',
  RESUME: 'RESUME'
};

export class StateManager {
  constructor(clientId) {
    this.clientId = clientId;
    this.storageKeyPrefix = `chatWidget_${clientId}_`;
    this.sessionId = null;
    this.messages = [];
    this.queuePosition = null;
    this.connectionStatus = 'disconnected';
    this.sessionState = SESSION_STATES.DISCONNECTED;  // Explicit session state
    this.isOpen = false;
    this.pendingMessages = [];
    this.sessionExpiry = 30 * 60 * 1000; // 30 minutes in milliseconds
    this.maxMessages = 50;
    this.config = null;  // Widget configuration
    this.uiManager = null;  // Set later for cross-tab UI updates
    this.messageTimeoutId = null;  // Track message timeout for cleanup
    this.onModalDismissed = null;  // Callback for modal dismissal (v1.0.43)

    // Cross-tab synchronization (v1.0.31) - BroadcastChannel with fallback
    this.bc = null;
    if ('BroadcastChannel' in window) {
      try {
        this.bc = new BroadcastChannel(`chatWidget_${clientId}`);
        this.bc.onmessage = (event) => this.handleCrossTabMessage(event.data);
        console.log('[State] BroadcastChannel initialized for cross-tab sync');
      } catch (error) {
        console.warn('[State] BroadcastChannel failed, using storage events:', error);
      }
    }

    // Fallback: Listen to storage events for older browsers
    if (!this.bc) {
      window.addEventListener('storage', (e) => {
        if (e.key === `${this.storageKeyPrefix}sync` && e.newValue) {
          try {
            const data = JSON.parse(e.newValue);
            this.handleCrossTabMessage(data);
          } catch (error) {
            console.error('[State] Failed to parse sync message:', error);
          }
        }
      });
      console.log('[State] Using storage events for cross-tab sync (fallback)');
    }
  }

  /**
   * Initialize state manager
   */
  init() {
    this.loadState();
    this.clearOldData();
  }

  /**
   * Load state from localStorage
   */
  loadState() {
    try {
      // Load session ID and check expiry
      const sessionData = this.getStorageItem('session');
      if (sessionData) {
        const { id, timestamp } = sessionData;
        const now = Date.now();

        if (now - timestamp < this.sessionExpiry) {
          this.sessionId = id;
        } else {
          // FIX v1.0.11: Session expired - clear session AND messages
          this.removeStorageItem('session');
          this.removeStorageItem('messages');  // Clear expired messages from storage
          this.sessionId = null;
          this.messages = [];  // Clear from memory too
          console.log('[State] Session expired - cleared session and messages');
        }
      }

      // FIX v1.0.38: Generate temp session_id immediately if none exists
      // This prevents localStorage race when multiple tabs open simultaneously
      if (!this.sessionId) {
        this.sessionId = `temp_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
        this.saveState();  // Save immediately for other tabs to find
        console.log('[State] Generated temp session_id:', this.sessionId, '(will be replaced by backend)');
      }

      // Load conversation history (only if session is still valid)
      if (this.sessionId) {
        const messages = this.getStorageItem('messages');
        if (messages && Array.isArray(messages)) {
          this.messages = messages.slice(-this.maxMessages);
        }
      }

      // Load widget state
      const widgetState = this.getStorageItem('widgetState');
      if (widgetState) {
        this.isOpen = widgetState.isOpen || false;
      }

      // Load pending messages
      const pendingMessages = this.getStorageItem('pendingMessages');
      if (pendingMessages && Array.isArray(pendingMessages)) {
        this.pendingMessages = pendingMessages;
      }

      console.log('[State] Loaded state:', {
        sessionId: this.sessionId,
        messageCount: this.messages.length,
        isOpen: this.isOpen,
        pendingCount: this.pendingMessages.length
      });

    } catch (error) {
      console.error('[State] Failed to load state:', error);
    }
  }

  /**
   * Save state to localStorage
   */
  saveState() {
    try {
      // Save session ID with timestamp
      if (this.sessionId) {
        this.setStorageItem('session', {
          id: this.sessionId,
          timestamp: Date.now()
        });
      }

      // Save messages (last 50)
      this.setStorageItem('messages', this.messages.slice(-this.maxMessages));

      // Save widget state
      this.setStorageItem('widgetState', {
        isOpen: this.isOpen,
        timestamp: Date.now()
      });

      // Save pending messages
      if (this.pendingMessages.length > 0) {
        this.setStorageItem('pendingMessages', this.pendingMessages);
      } else {
        this.removeStorageItem('pendingMessages');
      }

    } catch (error) {
      if (error.name === 'QuotaExceededError') {
        console.warn('[State] Storage quota exceeded, clearing old data');
        this.clearOldData();

        // Try again with reduced data
        try {
          this.messages = this.messages.slice(-25); // Keep only 25 messages
          this.saveState();
        } catch (retryError) {
          console.error('[State] Failed to save state even after cleanup:', retryError);
        }
      } else {
        console.error('[State] Failed to save state:', error);
      }
    }
  }

  /**
   * Add message to history
   */
  addMessage(text, isBot = false, metadata = {}) {
    const message = {
      id: `${Date.now()}_${Math.random()}`,
      text,
      isBot,
      timestamp: Date.now(),
      ...metadata
    };

    this.messages.push(message);

    // Keep only last N messages in memory
    if (this.messages.length > this.maxMessages * 2) {
      this.messages = this.messages.slice(-this.maxMessages);
    }

    // Save to localStorage
    this.saveState();

    // Broadcast to other tabs (v1.0.31)
    this.broadcastToTabs({
      type: 'message_received',
      message: message
    });

    return message;
  }

  /**
   * Remove last user message (for blocked/rejected messages)
   */
  removeLastUserMessage() {
    // Find and remove last user message (not bot)
    for (let i = this.messages.length - 1; i >= 0; i--) {
      if (!this.messages[i].isBot) {
        console.log('[State] Removing blocked user message:', this.messages[i].text.substring(0, 20));
        this.messages.splice(i, 1);
        this.saveState();
        return true;
      }
    }
    return false;
  }

  /**
   * Add pending message
   */
  addPendingMessage(text) {
    this.pendingMessages.push({
      text,
      timestamp: Date.now()
    });
    this.saveState();
  }

  /**
   * Get and clear pending messages
   */
  getPendingMessages() {
    const messages = [...this.pendingMessages];
    this.pendingMessages = [];
    this.saveState();
    return messages;
  }

  /**
   * Set session ID
   */
  setSessionId(sessionId) {
    this.sessionId = sessionId;
    this.saveState();
  }

  /**
   * Get session ID
   */
  getSessionId() {
    return this.sessionId;
  }

  /**
   * Check if session is valid
   */
  isSessionValid() {
    if (!this.sessionId) return false;

    const sessionData = this.getStorageItem('session');
    if (!sessionData) return false;

    const { timestamp } = sessionData;
    return Date.now() - timestamp < this.sessionExpiry;
  }

  /**
   * Set widget configuration
   */
  setConfig(config) {
    this.config = config;
  }

  /**
   * Get widget configuration
   */
  getConfig() {
    return this.config;
  }

  /**
   * Update queue position
   */
  setQueuePosition(position) {
    this.queuePosition = position;
  }

  /**
   * Update connection status
   */
  setConnectionStatus(status) {
    this.connectionStatus = status;
  }

  /**
   * Set session state with transition logging
   */
  setSessionState(newState) {
    const oldState = this.sessionState;

    // Validate state transition
    if (!Object.values(SESSION_STATES).includes(newState)) {
      console.error(`[State] Invalid state: ${newState}`);
      return;
    }

    // Update state
    this.sessionState = newState;
    console.log(`[State] ${oldState} → ${newState}`);

    // Save state change
    this.saveState();
  }

  /**
   * Get current session state
   */
  getSessionState() {
    return this.sessionState;
  }

  /**
   * Update widget visibility state
   */
  setWidgetOpen(isOpen) {
    this.isOpen = isOpen;
    this.saveState();

    // Broadcast to other tabs (v1.0.31)
    this.broadcastToTabs({
      type: isOpen ? 'widget_opened' : 'widget_closed'
    });
  }

  /**
   * Broadcast message to other tabs (v1.0.31)
   */
  broadcastToTabs(message) {
    if (this.bc) {
      // Use BroadcastChannel
      this.bc.postMessage(message);
    } else {
      // Fallback: Use storage event
      const key = `${this.storageKeyPrefix}sync`;
      localStorage.setItem(key, JSON.stringify(message));
      // Clear immediately to trigger event on next write
      setTimeout(() => localStorage.removeItem(key), 50);
    }
  }

  /**
   * Handle messages from other tabs (v1.0.31)
   */
  handleCrossTabMessage(data) {
    console.log('[State] Cross-tab message:', data);

    switch (data.type) {
      case 'message_received':
        // Another tab received a message - add to history if not duplicate
        if (!this.messages.find(m => m.id === data.message.id)) {
          this.messages.push(data.message);
          this.saveState();
          // UI update happens via WebSocket broadcast from backend
        }
        break;

      case 'session_expired':
        // Another tab detected expiry - clear local state
        console.log('[State] Session expired (notified by other tab)');
        this.resetSession();
        break;

      case 'widget_opened':
        this.isOpen = true;
        break;

      case 'widget_closed':
        this.isOpen = false;
        break;

      case 'modal_dismissed':
        // Another tab dismissed modal - trigger callback if set
        console.log('[State] Modal dismissed in another tab');
        if (this.onModalDismissed) {
          this.onModalDismissed();
        }
        break;
    }
  }

  /**
   * Get conversation history for restoration
   */
  getConversationHistory() {
    return this.messages.slice(-this.maxMessages);
  }

  /**
   * Clear old data (>30 days)
   */
  clearOldData() {
    try {
      const thirtyDaysAgo = Date.now() - (30 * 24 * 60 * 60 * 1000);
      const keysToRemove = [];

      // Find old keys
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);

        // Only process our widget keys
        if (key?.startsWith('chatWidget_')) {
          try {
            const data = localStorage.getItem(key);
            if (data) {
              const parsed = JSON.parse(data);
              if (parsed.timestamp && parsed.timestamp < thirtyDaysAgo) {
                keysToRemove.push(key);
              }
            }
          } catch (e) {
            // If we can't parse it, it might be corrupt - remove it
            keysToRemove.push(key);
          }
        }
      }

      // Remove old keys
      keysToRemove.forEach(key => {
        localStorage.removeItem(key);
      });

      if (keysToRemove.length > 0) {
        console.log(`[State] Cleared ${keysToRemove.length} old entries`);
      }

    } catch (error) {
      console.error('[State] Failed to clear old data:', error);
    }
  }

  /**
   * Reset session
   */
  resetSession() {
    this.sessionId = null;
    this.messages = [];
    this.queuePosition = null;
    this.pendingMessages = [];
    this.connectionStatus = 'disconnected';

    this.removeStorageItem('session');
    this.removeStorageItem('messages');
    this.removeStorageItem('pendingMessages');

    this.saveState();
  }

  /**
   * Storage helpers with error handling
   */
  getStorageItem(key) {
    try {
      const fullKey = this.storageKeyPrefix + key;
      const item = localStorage.getItem(fullKey);
      return item ? JSON.parse(item) : null;
    } catch (error) {
      console.error(`[State] Failed to get ${key}:`, error);
      return null;
    }
  }

  setStorageItem(key, value) {
    try {
      const fullKey = this.storageKeyPrefix + key;
      localStorage.setItem(fullKey, JSON.stringify(value));
    } catch (error) {
      throw error; // Re-throw to handle quota errors
    }
  }

  removeStorageItem(key) {
    try {
      const fullKey = this.storageKeyPrefix + key;
      localStorage.removeItem(fullKey);
    } catch (error) {
      console.error(`[State] Failed to remove ${key}:`, error);
    }
  }

  /**
   * Set message timeout ID for cleanup
   */
  setMessageTimeout(timeoutId) {
    // Clear existing timeout if any
    if (this.messageTimeoutId) {
      clearTimeout(this.messageTimeoutId);
    }
    this.messageTimeoutId = timeoutId;
  }

  /**
   * Clear and return message timeout ID
   */
  clearMessageTimeout() {
    if (this.messageTimeoutId) {
      clearTimeout(this.messageTimeoutId);
      this.messageTimeoutId = null;
    }
  }
}
